'use client'

import React, { useEffect, useState } from 'react'
import { Star, Send, CheckCircle } from 'lucide-react'
import { apiAddComment } from '@/apis/offers'
import { toast } from 'react-toastify'

interface Props {
    offer_id: string
    company_id: string
}

const CommentSection = ({ offer_id, company_id }: Props) => {
    const [rating, setRating] = useState(0)
    const [hover, setHover] = useState(0)
    const [comment, setComment] = useState('')
    const [loading, setLoading] = useState(false)
    const [hasRated, setHasRated] = useState(false)
    const [submitted, setSubmitted] = useState(false)

    useEffect(() => {
        const lastRated = localStorage.getItem(`hasRated_${offer_id}`)
        if (lastRated && new Date().getTime() - parseInt(lastRated) < 86400000) {
            setHasRated(true)
        }
    }, [offer_id])

    const labels = ['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent']

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (hasRated) { toast.info('You have already rated this product today.'); return }
        if (!rating && !comment.trim()) { toast.error('Please select a rating or add a comment.'); return }

        setLoading(true)
        try {
            const res = await apiAddComment(offer_id, company_id, comment, rating.toString())
            if (res.status === 'success') {
                localStorage.setItem(`hasRated_${offer_id}`, new Date().getTime().toString())
                setHasRated(true)
                setSubmitted(true)
                setRating(0)
                setComment('')
            } else {
                toast.error('Something went wrong. Please try again.')
            }
        } catch {
            toast.error('An error occurred while submitting.')
        } finally {
            setLoading(false)
        }
    }

    if (submitted || hasRated) {
        return (
            <div className="flex flex-col items-center gap-3 py-6 text-center">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center"
                    style={{ background: '#E8F0E8' }}>
                    <CheckCircle size={28} style={{ color: '#5C7A5C' }} />
                </div>
                <p className="text-sm font-black" style={{ fontFamily: 'Georgia, serif', color: '#2D3E2D' }}>
                    Thanks for your feedback!
                </p>
                <p className="text-xs" style={{ color: 'rgba(45,62,45,0.5)' }}>Your review has been submitted.</p>
            </div>
        )
    }

    return (
        <div>
            <h3 className="text-sm font-black mb-5 flex items-center gap-2" style={{ color: '#2D3E2D' }}>
                <Star size={14} style={{ color: '#F5C542' }} />
                Rate This Product
            </h3>

            <form onSubmit={handleSubmit} className="space-y-4">
                {/* Stars */}
                <div>
                    <div className="flex items-center gap-2 mb-1" onMouseLeave={() => setHover(0)}>
                        {[1, 2, 3, 4, 5].map(star => (
                            <button key={star} type="button"
                                onClick={() => setRating(star)}
                                onMouseEnter={() => setHover(star)}
                                className="transition-transform hover:scale-110">
                                <Star size={28} className={`transition-colors duration-150 ${star <= (hover || rating) ? 'text-yellow-400 fill-yellow-400' : 'fill-gray-200'
                                    }`} style={{ color: star <= (hover || rating) ? '#F5C542' : 'rgba(45,62,45,0.15)' }} />
                            </button>
                        ))}
                    </div>
                    {(hover || rating) > 0 && (
                        <span className="text-xs font-bold" style={{ color: '#5C7A5C' }}>
                            {labels[hover || rating]}
                        </span>
                    )}
                </div>

                {/* Comment */}
                <div>
                    <label className="block text-xs font-bold mb-2" style={{ color: 'rgba(45,62,45,0.6)' }}>
                        Your Comment <span className="font-normal" style={{ color: 'rgba(45,62,45,0.4)' }}>(optional)</span>
                    </label>
                    <textarea
                        value={comment}
                        onChange={e => setComment(e.target.value)}
                        placeholder="What did you think of this product?"
                        rows={3}
                        className="w-full px-4 py-3 text-sm rounded-xl focus:outline-none resize-none"
                        style={{
                            background: '#E8F0E8',
                            border: '1px solid rgba(92,122,92,0.2)',
                            color: '#2D3E2D'
                        }}
                    />
                </div>

                {/* Submit */}
                <button type="submit" disabled={loading || !rating}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold transition-all hover:shadow-lg disabled:opacity-50"
                    style={{ background: '#F5C542', color: '#2D3E2D' }}>
                    {loading ? (
                        <div className="w-4 h-4 border-2 rounded-full animate-spin"
                            style={{ borderColor: 'rgba(45,62,45,0.3)', borderTopColor: '#2D3E2D' }} />
                    ) : (
                        <><Send size={14} /> Submit Review</>
                    )}
                </button>
            </form>
        </div>
    )
}

export default CommentSection
